import React, {useEffect, useMemo, useReducer, useState} from "react";
import Alert from "@mui/material/Alert";
import Button from "@mui/material/Button";
import Chip from "@mui/material/Chip";
import Skeleton from "@mui/material/Skeleton";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import Typography from "@mui/material/Typography";
import SearchTwoToneIcon from "@mui/icons-material/SearchTwoTone";
import InputAdornment from "@mui/material/InputAdornment";
import clsx from "clsx";
import moment from "moment";
import {initReferenteState, REFERENTE_INITIAL_STATE, ReferenteReducer} from "../../../../reducers/ReferenteReducer";
import {getUsersByCompany} from "../../../../services/ReferenteServices";
import {getCompanyInfo} from "../../../../utils/GenericUtils";
import {useSessionStorage} from "../../../../utils/hooks/SessionStorageHook";
import SolidTextField from "../../../widgets/inputs/SolidTextField";

const formatDate = (value) => {
	const date = moment(value);
	return date.isValid() ? date.format("DD/MM/YYYY HH:mm") : "Non disponibile";
};

export const UtentiOnlineReport = ({classes}) => {
	const [state, dispatch] = useReducer(ReferenteReducer, REFERENTE_INITIAL_STATE, initReferenteState);
	const [filter, setFilter] = useSessionStorage("filterUser", false);
	const [value, setValue] = useState("");

	useEffect(() => {
		getUsersByCompany(getCompanyInfo()?.id, dispatch);
	}, [dispatch]);

	const filteredUsers = useMemo(() => {
		const search = value.trim().toUpperCase();
		return (state.users || [])
			.filter((item) => item.trusted)
			.filter((item) => (filter ? item.online : true))
			.filter((item) => !search || item.alias?.toUpperCase()?.includes(search) || item.username?.toUpperCase()?.includes(search))
			.sort((a, b) => (a.alias || "").localeCompare(b.alias || ""));
	}, [filter, state.users, value]);

	return (
		<section className={classes.panel}>
			<header className={classes.panelHeader}>
				<div>
					<Typography className={classes.panelTitle}>Utenti online</Typography>
					<Typography className={classes.panelSubtitle}>
						Monitora sessioni attive, ultimo accesso e scadenza sessione.
					</Typography>
				</div>
				<span className={classes.resultPill}>
					{filteredUsers.length} risultati
				</span>
			</header>

			<div className={classes.userToolbar}>
				<SolidTextField
					fullWidth
					onChange={(event) => setValue(event.target.value)}
					placeholder="Cerca per nome o username"
					size="small"
					value={value}
					variant="outlined"
					slotProps={{
						input: {
							startAdornment: (
								<InputAdornment position="start">
									<SearchTwoToneIcon fontSize="small"/>
								</InputAdornment>
							),
						},
					}}
				/>
				<Button
					className={classes.actionButton}
					onClick={() => setFilter(!filter)}
					size="small"
					variant={filter ? "contained" : "outlined"}
				>
					Solo online
				</Button>
			</div>

			{state.loadingGetUsers && (
				<Skeleton className={classes.skeletonCard} variant="rounded"/>
			)}

			{!state.loadingGetUsers && state.errorGetUsers !== "" && (
				<Alert severity="error" sx={{borderRadius: 4}}>
					Non e stato possibile recuperare gli utenti.
				</Alert>
			)}

			{!state.loadingGetUsers && state.errorGetUsers === "" && filteredUsers.length === 0 && (
				<Typography className={classes.emptyState}>Nessun utente trovato.</Typography>
			)}

			{!state.loadingGetUsers && state.errorGetUsers === "" && filteredUsers.length > 0 && (
				<TableContainer className={classes.tableCard}>
					<Table>
						<TableHead>
							<TableRow>
								<TableCell>Username</TableCell>
								<TableCell>Alias</TableCell>
								<TableCell>Status</TableCell>
								<TableCell>Ultimo accesso</TableCell>
								<TableCell>Scadenza sessione</TableCell>
							</TableRow>
						</TableHead>
						<TableBody>
							{filteredUsers.map((user) => (
								<TableRow hover key={user.id || user.username}>
									<TableCell>{user.username}</TableCell>
									<TableCell>{user.alias}</TableCell>
									<TableCell>
										{user.online ? (
											<Chip className={clsx(classes.statusPill, classes.statusPillOnline)} label="Online" size="small"/>
										) : (
											<span className={classes.statusPill}>Offline</span>
										)}
									</TableCell>
									<TableCell>{formatDate(user.ultimoAccesso)}</TableCell>
									<TableCell>{formatDate(user.scadenzaSessione)}</TableCell>
								</TableRow>
							))}
						</TableBody>
					</Table>
				</TableContainer>
			)}
		</section>
	);
};
